import type { Profile } from "../types/profile";

export function useAlgolia() {
   //
   /**
    * The `buildFacet` function takes an array of items and a facet string, then constructs facet-value
    * pairs based on the items and facet provided.
    * @param items - The `items` parameter in the `buildFacet` function is expected to be an array
    * containing any type of elements.
    * @param {string} facet - The `facet` parameter in the `buildFacet` function is a string that
    * represents the category or type of items being processed.
    * @returns The function `buildFacet` takes an array of items and a facet string as parameters. It then
    * checks the type of the items array and returns different values based on the conditions.
    */
   // NOTE: This may be better as a helper. I actually think I need to redo all of this to be more dynamic. I think a use algolia composable that I tap into would be the best way to go.
   const buildFacet = (
      items: number | string | unknown[] | undefined | null,
      facet: string,
   ): string | string[] | undefined => {
      if (items == null) return undefined;
      if (Array.isArray(items)) {
         if (items.length === 0) return undefined;
         if (items.length === 1) return `${facet}:${items[0]}`;
         return items.map((item) => `${facet}:${item}`);
      }
      if (typeof items === "string" || typeof items === "number") {
         return `${facet}:${items}`;
      }
      return undefined;
   };

   const buildGateFacet = (
      restrictors: string | Array<string>,
      facet: string
   ) => {
      // Return an empty array if no restrictors
      if (!restrictors) {
         return [];
      }
      // For a single string, return a one-element array
      if (typeof restrictors === "string") {
         return [`${facet}:${restrictors}`];
      }
      // If it’s already an array, map each item to a negative facet
      // e.g. ['8-9','10-11'] → ['-8-9:intended_ages','-10-11:intended_ages']
      return restrictors.map((item) => `${facet}:${item}`);
   };

   /**
    * Filters a list of cards based on the user's school and school group information.
    *
    * The function first removes any cards that do not have at least one of the following:
    * - The user's school name included in the card's 'schools' array.
    * - The user's school group included in the card's 'school_groups' array.
    * - Both 'schools' and 'school_groups' arrays are empty.
    *
    * If a 'userAges' array is provided, it further filters out any cards where the card's
    * 'intended_ages' is present in the 'userAges' array. Cards without 'intended_ages'
    * are retained.
    *
    * @param cards - An array of card objects to filter.
    * @param user - A user profile object containing metadata, including the user's school and group.
    * @param userAges - An optional array of age strings to further filter the cards.
    * @returns An array of cards that match the filtering criteria.
    */
   const filterCards = <T extends Record<string, unknown>>(
      cards: T[],
      user: Profile.LoginUser | Profile.MetaData,
      userAges?: string[],
   ): T[] => {
      if (!cards?.length) return [];
      const cleanCards = cards.filter((card) => {
         const schools = card?.schools as string[] | undefined;
         const schoolGroups = card?.school_groups as string[] | undefined;
         return (
            (user.school?.name && schools?.includes(user.school.name)) ||
            (user.school?.group && schoolGroups?.includes(user.school.group)) ||
            (schoolGroups?.length === 0 && schools?.length === 0)
         );
      });

      if (userAges && userAges.length) {
         return cleanCards.filter((card) => {
            const ages = card.intended_ages as string | undefined;
            return !ages || !userAges.includes(ages);
         });
      }

      return cleanCards;
   };

   return {
      buildFacet,
      buildGateFacet,
      filterCards,
   };
}
